import { Box, styled } from '@mui/system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const NavExpandRoot = styled('div')(() => ({
  marginTop: 14,
  '&:first-of-type': {
    marginTop: 6,
  },
  '& .expandIcon': {
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: 'rotate(0deg)',
  },
  '& .collapseIcon': {
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: 'rotate(-90deg)',
  },
  '& .expansion-panel': {
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(1),
  minHeight: 'auto',
  paddingTop: 4,
  paddingBottom: 8,
  paddingRight: 2,
  paddingLeft: 12,
  marginBottom: 4,
  cursor: 'pointer',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  color: theme.palette.text.secondary,
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  outline: 'none',
  font: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: 2,
    borderRadius: 4,
  },
  '&.compactNavItem': {
    width: 42,
    minWidth: 42,
    maxWidth: 42,
    overflow: 'hidden',
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    '& .header-rule': {
      display: 'none',
    },
  },
}));

/** Horizontal rule between section title and chevron */
const HeaderRule = styled('span')(({ theme }) => ({
  flex: 1,
  minWidth: theme.spacing(2),
  height: 1,
  alignSelf: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
}));

const HeadingTitle = styled('span')(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.09em',
  lineHeight: 1.4,
  textTransform: 'uppercase',
  color: 'inherit',
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const BadgeValue = styled('span')(({ theme }) => ({
  padding: '2px 7px',
  borderRadius: '999px',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.02em',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

/** Small disclosure caret — not a nav icon, no icon font */
const DisclosureCaret = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  borderTop: `5px solid ${theme.palette.text.secondary}`,
  opacity: 0.55,
  flexShrink: 0,
}));

const MatxVerticalNavExpansionPanel = ({ item, children, sidebarExpanded }) => {
  /** collapsed === true means section is closed */
  const [collapsed, setCollapsed] = useState(false);
  const elementRef = useRef(null);
  const { pathname } = useLocation();
  const { name, badge } = item;

  const handleToggle = () => {
    setCollapsed((prev) => !prev);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  useEffect(() => {
    const root = elementRef.current;
    if (!root?.children?.length) return;

    for (const child of root.children) {
      const linkElement = child.querySelector?.('a');
      if (!linkElement) continue;
      const hrefValue = linkElement.getAttribute('href');
      if (hrefValue === pathname) {
        setCollapsed(false);
        break;
      }
    }
  }, [pathname]);

  return (
    <NavExpandRoot>
      <SectionHeader
        component="button"
        type="button"
        className={clsx({ compactNavItem: !sidebarExpanded })}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={!collapsed}
      >
        <HeadingTitle>{name}</HeadingTitle>

        <HeaderRule className="header-rule" aria-hidden />

        <Box display="flex" alignItems="center" gap={0.75} flexShrink={0}>
          {badge && <BadgeValue>{badge.value}</BadgeValue>}
          <DisclosureCaret
            className={clsx({
              collapseIcon: collapsed,
              expandIcon: !collapsed,
            })}
            aria-hidden
          />
        </Box>
      </SectionHeader>

      <Box sx={{ mb: 0.5 }}>
        <div
          ref={elementRef}
          className="expansion-panel submenu"
          style={{
            maxHeight: collapsed ? 0 : 3200,
          }}
        >
          {children}
        </div>
      </Box>
    </NavExpandRoot>
  );
};

export default MatxVerticalNavExpansionPanel;
